import { View } from "extensions/marionette";
import $ from "jquery";
import Radio from "backbone.radio";
import { captureException } from "extensions/sentry";
import { toast } from "extensions/toast";

import { nextTick, destroyPopovers } from "common/utils";
import { emitter } from "common/emitter";

import { AllFilters } from "./allFilters/allFilters";
import { SavedFiltersList } from "./savedFilters/savedFiltersList";
import { EmptySavedFilters } from "./savedFilters/emptySavedFilters";
import { Qualifiers } from "../qualifiers";
import filtersMenuTemplate from "./filtersMenu.jst";

const actionParser = (str) => str.split(":");

export const FiltersMenu = View.extend({
    className() {
        if (this.getOption("alwaysShowFilters")) {
            return "filters-menu always-shown";
        }
        return "filters-menu";
    },
    template: filtersMenuTemplate,
    regions: {
        content: ".view"
    },
    
    childViewTriggers: {
        "add:filter": "add:filter", // Proxy add event from allFilters view
        "clear:temp:filters": "clear:temp:filters" // Proxy clean event from allFilters view
    },
    
    childViewEvents: {
        action(str) {
            this.handleAction(actionParser(str));
        }
    },
    
    events: {
        "click [data-action]": function(e) {
            e.preventDefault();
            this.handleAction(actionParser(e.currentTarget.dataset.action));
        }
    },
    
    initialize() {
        this.state = {
            hidden: !this.getOption("alwaysShowFilters"),
            seen: false,
            view: "all"
        };
        
        this.listenTo(Radio.channel("main").request("get:filters"), "add remove update", this.render);
    },
    
    handleAction([action, value] = []) {
        if (!action) {
            throw new Error("Action is empty");
        }
        
        if (action === "show" && typeof value === "string") {
            this.state.view = value;
            this.render();
        }
        
        if (action === "apply" && typeof value === "string") {
            this.applySavedFilter({ id: value });
        }
        
        if (action === "delete" && typeof value === "string") {
            this.deleteSavedFilter({ id: value });
        }
    },
    
    hide() {
        this.state.hidden = true;
        this.$el.addClass("hidden");
        this.unbindClickOutside();
        if (this.getOption("parent")) {
            this.getOption("parent").$el.removeClass("filters-open");
            this.getOption("parent").$(".chevron-menu-icon").removeClass("fa-chevron-up").addClass("fa-chevron-down");
        }
        
        if (document.body.dataset.noScrollSource === "filter-bar-v1" && document.body.classList.contains("noscroll")) {
            document.body.classList.remove("noscroll");
            delete document.body.dataset.noScrollSource;
        }
    },
    
    show() {
        this.state.hidden = false;
        
        // Render initial screen if we haven't seen the menu yet
        if (this.state.seen === false) {
            this.render();
            this.state.seen = true;
        }
        
        this.$el.removeClass("hidden");
        
        if (!this.getOption("alwaysShowFilters")) {
            nextTick(() => {
                $(document).off(`click.${this.cid}`).on(`click.${this.cid}`, (e) => {
                    if (!this.getOption("parent").el.contains(e.target) && e.target.closest("body") !== null) {
                        this.hide();
                    }
                });
            });
        }
        
        if (this.getOption("parent")) {
            this.getOption("parent").$el.addClass("filters-open");
            this.getOption("parent").$(".chevron-menu-icon").removeClass("fa-chevron-down").addClass("fa-chevron-up");
        }

        // Check if filtersmenu
        if (document.getElementsByClassName("modal").length === 0) {
            // Filters menu full screen calculation
            this.el.style.height = `${document.documentElement.clientHeight - this.el.getBoundingClientRect().top}px`;
    
            if (!document.body.classList.contains("noscroll")) {
                document.body.classList.add("noscroll");
                document.body.dataset.noScrollSource = "filter-bar-v1";
            }
        }
    },
    
    renderAllFilters() {
        this.showChildView("content", new AllFilters({
            parent: this.getOption("parent")
        }));
    },
    
    renderSavedFilters() {
        const collection = Radio.channel("main").request("get:filters");
        
        if (collection.length) {
            this.showChildView("content", new SavedFiltersList({
                collection
            }));
        } else {
            this.showChildView("content", new EmptySavedFilters());
        }
    },
    
    applySavedFilter({ id }) {
        const filterToApply = Radio.channel("main").request("get:filters").get(id);
    
        if (!filterToApply) {
            throw new Error(`Cannot apply filter id: ${id}`);
        }
        
        try {
            this.triggerMethod("apply:saved:filter", new Qualifiers({
                value: filterToApply.get("qualifiers")
            }));
    
            emitter.emit("tracking.track", {
                type: "filter_applied",
                data: {
                    name: filterToApply.get("name"),
                    qualifiers: filterToApply.get("qualifiers")
                }
            });
        } catch (error) {
            toast.error("There was an error applying your saved filter");
            captureException(error);
        }
    },
    
    deleteSavedFilter({ id }) {
        const filterToDestroy = Radio.channel("main").request("get:filters").get(id);
        
        if (!filterToDestroy) {
            throw new Error(`Cannot destroy filter id: ${id}.`);
        }
    
        destroyPopovers();
        filterToDestroy.destroy({
            wait: true
        }).then(() => {
            toast.success("Your filter is removed");
    
            // re-render the parent to reflect new state
            this.getOption("parent").render();
    
            emitter.emit("tracking.track", {
                type: "filter_deleted",
                data: {
                    name: filterToDestroy.get("name"),
                    qualifiers: filterToDestroy.get("qualifiers")
                }
            });
        }).catch(() => {
            toast.error("There was an error removing your filter");
        });
    },
    
    onBeforeRender() {
        if (this.state.hidden) {
            this.hide();
        }
    },
    
    onBeforeDestroy() {
        this.unbindClickOutside();
    },
    
    unbindClickOutside() {
        $(document).off(`click.${this.cid}`);
    },
    
    makeSidebarSavedFilters() {
        if (!this.getOption("savedFilters")) return [];
        
        const allFilters = Radio.channel("main").request("get:filters");
        
        return allFilters.getRecent();
    },
    
    onRender() {
        if (this.state.hidden) {
            return;
        }
        
        if (this.state.view === "all") {
            this.renderAllFilters();
        } else if (this.state.view === "saved") {
            this.renderSavedFilters();
        } else {
            throw new Error(`Unknown view: ${this.state.view}`);
        }
    },
    
    templateContext() {
        return {
            state: this.state,
            savedFilters: this.getOption("savedFilters"),
            filters: this.makeSidebarSavedFilters()
        };
    }
});
